package com.downdogapp.client.controllers.start

import com.downdogapp.*
import com.downdogapp.client.*
import com.downdogapp.client.api.*
import com.downdogapp.client.controllers.*
import com.downdogapp.client.resources.Images
import com.downdogapp.client.singleton.*
import com.downdogapp.client.views.start.StartView

object StartViewController : ViewController() {

  val allPages =
    listOf(
      NewPracticePage,
      OfflinePracticesPage,
      StatsPage,
      HistoryPage,
      MenuPage
    )

  var currentPageIndex: Int = 0
    private set(value) {
      if (field != value) {
        allPages[field].onScrolledFrom()
        allPages[value].onScrolledTo()
      }
      field = value
    }

  private var loggingIn = false
  private var isMessageRequestInFlight = false

  val selectedPage: Page get() = allPages[currentPageIndex]

  override val view = StartView()

  val visiblePages get() = allPages.filter { it.shouldDisplay }

  init {
    refreshView()
  }

  fun onVisiblePageSelected(visibleIndex: Int) {
    logEvent("go_to_page", mapOf("page" to visiblePages[visibleIndex].title))
    val pageIndex = allPages.indexOf(visiblePages[visibleIndex])
    currentPageIndex = pageIndex
  }

  fun isPageVisible(index: Int) = allPages[index] in visiblePages

  override fun onViewBecameVisible() {
    logEvent("became_visible")
    if (App.isBrowserPhone()) {
      App.alert(message = Strings.SAFARI_OR_MOBILE_BROWSER_WARNING_MESSAGE)
    }

    if (MANIFEST.displayLoginOnStart) {
      logEvent("displaying_login_on_start")
      App.logoutAccounts()
      promptSignIn()
    } else {
      view.hideLoadingOverlay()
      NewPracticePage.animateIn()
      maybePromptLinkedSequence {
        maybePromptResumePractice {
          maybeShowStartMessage()
          logEvent("finished_became_visible")
        }
      }
    }
  }

  fun promptOnboarding(onCompletion: () -> Unit) {
    logEvent("prompt_onboarding")
    showOnboardingSelector(0, onCompletion)
  }

  private fun showOnboardingSelector(index: Int, onCompletion: () -> Unit) {
    val onboardingSelectors = MANIFEST.onboardingConfig!!.selectorItems
    if (index >= onboardingSelectors.size) {
      onCompletion()
    } else {
      val item = onboardingSelectors[index]
      when {
        item.typeId == MANIFEST.mixPresetTypeId -> SequenceSettings.getMixConfig().let { mixConfig ->
          if (mixConfig == null)
            showOnboardingSelector(index + 1, onCompletion)
          else
            App.pushViewController(
              MixViewController(
                selectorItem = item,
                onboardingScreenIndex = index,
                numOnboardingScreens = onboardingSelectors.size,
                onFinishFn = { showOnboardingSelector(index + 1, onCompletion) }
              )
            )
        }

        SequenceSettings.getOptions(item.typeId).size <= 1 -> {
          showOnboardingSelector(index + 1, onCompletion)
        }

        else -> {
          App.pushViewController(
            SelectorViewController(
              settingTypeId = item.typeId,
              onOptionSelected = { showOnboardingSelector(index + 1, onCompletion) },
              title = item.selectorTitle,
              subtitle = item.selectorSubtitle,
              onboardingScreenIndex = index,
              numOnboardingScreens = onboardingSelectors.size
            )
          )
        }
      }
    }
  }

  private fun maybePromptLinkedSequence(callback: () -> Unit = {}) =
    if (UserPrefs[Key.LinkedSequenceId] != null) {
      promptLinkedSequence(
        sequenceId = UserPrefs[Key.LinkedSequenceId]!!,
        callback = {
          UserPrefs.remove(Key.LinkedSequenceId)
          callback()
        }
      )
    } else {
      callback()
    }

  fun promptLinkedSequence(sequenceId: String, callback: () -> Unit = {}) {
    logEvent("prompting_linked_sequence")
    App.alert(
      title = Strings.SHARED_PRACTICE,
      message = Strings.START_SHARED_PRACTICE,
      negativeButton = AlertAction(Strings.CANCEL) {
        callback()
      },
      positiveButton = AlertAction(Strings.YES_STRING) {
        logEvent("start_linked_sequence", mapOf("sequenceId" to sequenceId))
        App.goToExistingSequence(sequenceId)
      }
    )
  }

  private fun maybePromptResumePractice(callback: () -> Unit = {}) {
    if (MANIFEST.sequenceIdToResume != null && !Network.isOffline()) {
      logEvent("prompting_resume_practice")
      val sequenceId = MANIFEST.sequenceIdToResume!!
      App.alert(
        title = Strings.RESUME_PRACTICE,
        message = Strings.RESUME_PRACTICE_MESSAGE,
        negativeButton = AlertAction(Strings.NO_STRING) {
          logEvent("declined_resume_practice")
          showSpinner()
          Network.post(RecordPracticeFinishedRequest(sequenceId = sequenceId, offline = false)) {
            hideSpinner()
            callback()
          }
        },
        positiveButton = AlertAction(Strings.YES_STRING) {
          logEvent(
            "resume_practice",
            mapOf("sequenceId" to sequenceId)
          )
          App.goToExistingSequence(sequenceId)
        }
      )
    } else {
      callback()
    }
  }

  override fun onUnwind() {
    refreshView()
    if (!loggingIn) {
      maybeShowStartMessage()
    }
    // shouldn't be necessary but helps fix stuff after rotating on iOS
    App.runAfter(seconds(0.1)) {
      refreshView()
    }
  }

  private fun maybeShowStartMessage() {
    if (!isMessageRequestInFlight) {
      isMessageRequestInFlight = true
      Network.post(MessageRequest()) { json ->
        json?.let {
          handleMessageResponse(
            response = MessageResponse.fromJson(it),
            feedbackType = FeedbackType.OTHER
          )
        }
        isMessageRequestInFlight = false
      }
    }
  }

  fun handleMessageResponse(response: MessageResponse,
                            feedbackType: FeedbackType,
                            sequenceId: String? = null,
                            sequenceTime: Duration? = null) {
    when {
      response.message != null -> {
        App.pushViewController(
          MessageViewController(
            message = response.message,
            feedbackType = feedbackType,
            sequenceId = sequenceId,
            sequenceTime = sequenceTime
          )
        )
      }

      response.survey != null -> {
        App.pushViewController(
          SelectorViewController(
            title = response.survey.question,
            options = response.survey.surveyAnswerIds.zip(response.survey.surveyAnswers)
              .map { (id, answer) ->
                SelectorOption(id = id, label = answer)
              },
            initialSelectedId = response.survey.surveyAnswerIds.first(),
            onOptionSelected = {
              Network.post(RecordSurveyAnswerRequest(response.survey.id, it))
            }
          )
        )
      }

      response.urlToOpen != null -> {
        App.openUrl(response.urlToOpen)
      }

      response.displayMembership -> {
        App.goToPurchase()
      }

      else -> {}
    }
  }

  fun refreshView() {
    visiblePages.forEach { it.refreshPage() }
    view.refreshView()
    if (selectedPage !in visiblePages) {
      goToPage(visiblePages.first())
    }
  }

  fun goToPage(page: Page) {
    logEvent("go_to_page", mapOf("page" to page.title))
    currentPageIndex = allPages.indexOf(page)
    view.goToPage(page)
  }

  fun promptSignIn() {
    loggingIn = true
    App.pushViewController(
      WelcomeViewController {
        goToPage(visiblePages.first())
        App.unwindToViewController(StartViewController::class)
        view.hideLoadingOverlay()
        NewPracticePage.animateIn()
        if (NewPracticePage == visiblePages.first()) {
          NewPracticePage.showDrawerTooltip {
            maybePromptResumePractice {
              maybeShowStartMessage()
              loggingIn = false
            }
          }
        }
      }
    )
  }

  override fun onBackClicked() {
    if (!selectedPage.onBackClicked()) {
      if (currentPageIndex > 0) {
        goToPage(visiblePages.first())
      } else {
        App.exit()
      }
    }
  }

  override fun onForegrounded() {
    selectedPage.onForegrounded()
  }

  fun showSpinner() {
    view.showSpinner()
  }

  fun hideSpinner() {
    view.hideSpinner()
  }

  fun setTabBarOpacity(value: Number) =
    view.setTabBarOpacity(value)

  fun getTabBarIcon(page: Page) = when (page) {
    NewPracticePage -> Images.HOME_ICON
    StatsPage -> Images.JOURNEYS_ICON
    HistoryPage -> Images.HISTORY_ICON
    OfflinePracticesPage -> Images.OFFLINE_ICON
    MenuPage -> Images.MENU_ICON
    else -> error("Could not find TabBarIcon for page: $page")
  }
}
